import { React, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MSpinner, Input, Container, Flex, Text, Heading, Drawer, Button, Empty } from "@cisco/spaces-dash-lib-styleguide";
import { v4 as uuidv4 } from "uuid";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import useLoader from "../../hooks/useLoader";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import "./floor-map.scss";
import { I18nContext } from "../../contexts/i18nContext";
import PolygonIcon from "../../assets/images/location-map/polygon_zone.svg";
import APIcon from "../../assets/images/location-map/ap-icon.svg";
import useNotification from "../../hooks/useNotification";

function FloorMap(props) {
  const i18n = useContext(I18nContext);
  const navigate = useNavigate();
  const { nodeData, locationRowMap, mapboxApiKey, setZoneDrawerOpen } = props;
  const { setToastMessage } = useNotification();
  const [draw, setDraw] = useState();
  const [selZoneData, setSelZoneData] = useState();
  const [zoneFormAction, setZoneFormAction] = useState("create");
  const [newZoneCoordinates, setNewZoneCoordinates] = useState({
    vertices: 0
  });
  const [zoneForm, setZoneForm] = useState({
    name: "",
    color: "",
    totalArea: "",
    totalCapacity: ""
  });
  const [floorMap, setFloorMap] = useState({});
  const [floorLoader, setFloorLoader] = useState(false);
  const [floorImageMissing, setFloorImageMissing] = useState(false);
  const [showZoneForm, setShowZoneForm] = useState(false);
  let currentAPMarkers = [];
  let polyGeoMarkers = [];
  let bounds;
  let dimensions = {
    width: 0,
    length: 0
  };

  const defaultZoneColor = "#3498db";

  const allZones = [];

  const zoneColors = ["#A0D7FF", "#DB5C89", "#74D2DD", "#3E7FFF", "#DA9EDD", "#FFF19A", "#FFD59B", "#87BBF4", "#63B9C3", "#D174E1", "#FFE755", "#FFB859", "#587AC2", "#BE36BE", "#F6BD26", "#FA9322"];
  const { setLoading } = useLoader();

  const resetFloorData = () => {
    setFloorImageMissing(false);
    currentAPMarkers = [];
    polyGeoMarkers = [];
  };

  // bounds to coordinates
  const boundsToCoordinates = (b, p = 0) => {
    return [
      [b[0][0] - p, b[1][1] - p], // top left
      [b[1][0] - p, b[1][1] - p], // top right
      [b[1][0] - p, b[0][1] - p], // bottom right
      [b[0][0] - p, b[0][1] - p] // bottom left
    ];
  };

  // function to convert APs coordinates x,y (in feet) into lat long coordinates that can be plotted on map
  const unproject = (point) => {
    const boundsCpy = bounds;
    const dimensionsCpy = { ...dimensions };

    if (!boundsCpy || !dimensionsCpy) return { lng: 0, lat: 0 };

    const x = point.x || 0;
    const y = point.y || 0;
    const mapWidth = boundsCpy.getEast() - boundsCpy.getWest();
    const mapHeight = boundsCpy.getNorth() - boundsCpy.getSouth();
    const floorWidth = dimensionsCpy.width;
    const floorHeight = dimensionsCpy.length;

    return {
      lng: boundsCpy.getWest() + (x / floorWidth) * mapWidth,
      lat: boundsCpy.getSouth() + (1 - y / floorHeight) * mapHeight
    };
  };

  // function to convert floor lat long coordinates into x,y points relative to the current floor map
  const project = (lngLat) => {
    const boundsCpy = bounds;
    const dimensionsCpy = { ...dimensions };
    if (!boundsCpy || !dimensionsCpy) return { x: 0, y: 0 };

    const lng = lngLat.lng || 0;
    const lat = lngLat.lat || 0;
    const mapWidth = boundsCpy.getEast() - boundsCpy.getWest();
    const mapHeight = boundsCpy.getNorth() - boundsCpy.getSouth();
    const floorWidth = dimensionsCpy.width;
    const floorHeight = dimensionsCpy.length;

    return {
      x: ((lng - boundsCpy.getWest()) / mapWidth) * floorWidth,
      y: (1 - (lat - boundsCpy.getSouth()) / mapHeight) * floorHeight
    };
  };

  // function called on any polygon zone action - add, delete, update
  const addArea = (e) => {
    if (e && e.features && e.features.length && e.features[0].geometry && e.features[0].geometry.coordinates && e.features[0].geometry.coordinates[0] && e.features[0].geometry.coordinates[0].length) {
      const newZoneCoordinatesCpy = { ...newZoneCoordinates };
      newZoneCoordinatesCpy.vertices = e.features[0].geometry.coordinates[0].length;
      const coordsData = e.features[0].geometry.coordinates[0];
      coordsData.forEach((coordinates, i) => {
        const newCoords = project({
          lng: coordinates[0],
          lat: coordinates[1]
        });

        const coordObj = {
          x: newCoords.x || 0,
          y: newCoords.y || 0,
          z: 0,
          unit: null
        };

        const vertexKey = `v${i}`;
        newZoneCoordinatesCpy[vertexKey] = coordObj;
      });
      console.log("new zone coordinates", newZoneCoordinatesCpy);
      setNewZoneCoordinates(newZoneCoordinatesCpy);
      setShowZoneForm(true);
      setZoneDrawerOpen(true);
      setZoneFormAction("create");
    }
  };

  const updateArea = (e) => {
    console.log(e);
    const data = draw.getAll();
    console.log("Polygon Zone Data::", data);
  };

  const deleteArea = (e) => {
    console.log(e);
    const data = draw.getAll();
    console.log("Polygon Zone Data::", data);
  };

  const selectZoneColor = (color) => {
    const zoneFormCpy = { ...zoneForm };
    zoneFormCpy.color = color;
    setZoneForm(zoneFormCpy);
  };

  const updateZoneName = (name) => {
    const zoneFormCpy = { ...zoneForm };
    zoneFormCpy.name = name;
    setZoneForm(zoneFormCpy);
  };

  const resetZoneForm = () => {
    const zoneFormCpy = {
      name: "",
      color: "",
      totalArea: "",
      totalCapacity: ""
    };

    setZoneForm(zoneFormCpy);
  };

  // <div class="zone-input-group">
  //   <label>{{ 'business-hierarchy-lib.location-map.zone-name-text' | translate }}</label>
  //   <input type="text" [(ngModel)]="zoneForm.name" />
  // </div>
  // <div class="zone-color-picker">
  //   <label>{{ 'business-hierarchy-lib.location-map.overlay-color' | translate }}</label>
  //   <span *ngFor="let color of zoneColors; let i = index;">
  //     <span (click)="selectZoneColor(color)" class="zone-color"
  //       [ngStyle]="{'background-color': color, 'border': zoneForm.color === color ? '1px solid #000': '0' }"></span>
  //   </span>
  // </div>
  // <!-- <div class="zone-input-group">
  // 	<label>Total Area (sq. ft)</label>
  // 	<input type="text" [(ngModel)]="zoneForm.totalArea" />
  // </div>
  // <div class="zone-input-group">
  // 	<label>Occupancy Limit (Max. Capacity)</label>
  // 	<input type="text" [(ngModel)]="zoneForm.totalCapacity" />
  // </div> -->
  // <div class="zone-form-actions">
  //   <button class="cancel-zone"
  //     (click)="deleteFeature()">{{ 'business-hierarchy-lib.business-location-details.cancel-text' | translate }}</button>
  //   <button class="save-zone" *ngIf="zoneFormAction === 'update'" (click)="saveZone()">
  //     {{ 'business-hierarchy-lib.hierarchy-nodes.save-text' | translate }}
  //   </button>
  //   <button class="save-zone" *ngIf="zoneFormAction === 'create'" (click)="createZone()">
  //     {{ 'business-hierarchy-lib.hierarchy-nodes.save-text' | translate }}
  //   </button>
  // </div>

  const cancelZone = () => {
    console.log("Cancel Zone::", selZoneData);
    setShowZoneForm(false);
    setZoneDrawerOpen(false);
    setSelZoneData(null);
    resetZoneForm();
  };

  const deleteFeature = () => {
    const id = draw.getSelectedIds();
    draw.delete(id);
    cancelZone();
  };

  const createZone = () => {
    const node = nodeData.data;
    console.log("Create Zone::", selZoneData);
    if (zoneForm.name === "") {
      // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.provide-zone-name")}</MNotification>;
      setToastMessage(i18n.t("location-map.provide-zone-name"), 5000, "negative");
      return;
    }
    const saveZonePayload = {
      name: zoneForm.name,
      level: "ZONE",
      details: {},
      internalData: {}
    };

    saveZonePayload.details = newZoneCoordinates;
    saveZonePayload.details.internalData = {
      color: zoneForm.color
    };
    if (node.maps[0].source === "MERAKI") {
      saveZonePayload.type = "meraki";
    }
    // saveZonePayload.internalData['totalArea'] = this.zoneForm.totalArea;
    // saveZonePayload.internalData['totalCapacity'] = this.zoneForm.totalCapacity;

    console.log("saveZonePayload::", saveZonePayload);
    setLoading(true);
    const floorId = node.id;
    BusinessHierarchyService.createPolygonZone([saveZonePayload], floorId)
      .then((data) => {
        if (data && data.status) {
          cancelZone();
          // setLoading(false);
          setToastMessage(i18n.t("location-map.zone-creation-in-progress"), 5000, "positive");
          // <MNotification status="positive">{i18n.t("business-hierarchy-lib.location-map.zone-creation-in-progress")}</MNotification>;
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          cancelZone();
          setLoading(false);
          setToastMessage(i18n.t("location-map.error-on-creating-zone"), 5000, "negative");
          // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.error-on-creating-zone")}</MNotification>;
        }
      })
      .catch((error) => {
        console.log(error);
        cancelZone();
        setLoading(false);
        setToastMessage(i18n.t("location-map.error-on-creating-zone"), 5000, "negative");
        // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.error-on-creating-zone")}</MNotification>;
      });
  };

  const saveZone = () => {
    const node = nodeData.data;
    console.log("Save Zone::", selZoneData);
    if (zoneForm.name === "") {
      setToastMessage(i18n.t("location-map.provide-zone-name"), 5000, "negative");
      // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.provide-zone-name")}</MNotification>;
      return;
    }
    const saveZonePayload = {
      name: zoneForm.name,
      level: "ZONE",
      details: {}
    };

    // {"name":"sfddf","level":"ZONE","details":{"vertices":5,"v0":{"x":66.472496,"y":62.78252,"z":0,"unit":null},"v1":{"x":109.2448,"y":68.11639,"z":0,"unit":null},"v2":{"x":107.106186,"y":91.05364,"z":0,"unit":null},"v3":{"x":65.93784,"y":91.5871,"z":0,"unit":null},"v4":{"x":66.472496,"y":62.78252,"z":0,"unit":null},"internalData":{}}}

    if (selZoneData.zoneCoordinates && selZoneData.zoneCoordinates.length) {
      saveZonePayload.details.vertices = selZoneData.zoneCoordinates.length;
      selZoneData.zoneCoordinates.forEach((coords, i) => {
        if (coords) {
          const coordsArr = coords.split(",");
          const coordObj = {
            x: coordsArr[0] ? parseFloat(coordsArr[0]) : 0.0,
            y: coordsArr[1] ? parseFloat(coordsArr[1]) : 0.0,
            z: coordsArr[2] ? parseFloat(coordsArr[2]) : 0.0,
            unit: null
          };
          const vertexKey = `v${i}`;
          saveZonePayload.details[vertexKey] = coordObj;
        }
      });

      saveZonePayload.details.internalData = {
        color: zoneForm.color
      };
      if (node.maps[0].source === "MERAKI") {
        saveZonePayload.type = "meraki";
      }
      // saveZonePayload.internalData['totalArea'] = this.zoneForm.totalArea;
      // saveZonePayload.internalData['totalCapacity'] = this.zoneForm.totalCapacity;

      console.log("saveZonePayload::", saveZonePayload);
      setLoading(true);
      BusinessHierarchyService.updatePolygonZone(saveZonePayload, selZoneData.id)
        .then((data) => {
          if (data && data.status) {
            cancelZone();
            setLoading(false);
            setToastMessage(i18n.t("location-map.zone-update-in-progress"), 5000, "postive");
            // <MNotification status="positive">{i18n.t("business-hierarchy-lib.location-map.zone-update-in-progress")}s</MNotification>;
          } else {
            cancelZone();
            setLoading(false);
            setToastMessage(i18n.t("location-map.error-on-updating-zone"), 5000, "negative");
            // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.error-on-updating-zone")}</MNotification>;
          }
        })
        .catch((error) => {
          console.log(error);
          cancelZone();
          setLoading(false);
          setToastMessage(i18n.t("location-map.error-on-updating-zone"), 5000, "negative");
          // <MNotification status="negative">{i18n.t("business-hierarchy-lib.location-map.error-on-updating-zone")}</MNotification>;
        });
    } else {
      // zone error
    }
  };

  // Constants
  const MARKER_SIZE = "30px";
  const DEFAULT_ZONE_COLOR = defaultZoneColor; // Ensure `defaultZoneColor` is defined

  // Create a function to set up the marker's HTML
  const createMarkerElement = () => {
    const el = document.createElement("div");
    el.className = "polymarker";
    el.style.backgroundImage = `url(${PolygonIcon})`;
    el.style.width = MARKER_SIZE;
    el.style.height = MARKER_SIZE;
    el.style.backgroundSize = "100%";
    return el;
  };

  // Create a function to handle the click event
  const handleMarkerClick =
    ({ zoneData }) =>
    () => {
      setSelZoneData(zoneData);
      resetZoneForm();

      const { zoneDetails } = zoneData;
      const zoneFormCpy = {
        ...zoneForm,
        name: zoneData.name,
        color: zoneDetails.color || "",
        totalArea: zoneDetails.totalArea || "",
        totalCapacity: zoneDetails.totalCapacity || ""
      };

      setZoneForm(zoneFormCpy);
      setShowZoneForm(true);
      setZoneDrawerOpen(true);
      setZoneFormAction("update");
    };

  // Create a function to set up the popup content
  const createPopupHTML = (name, color) => `
  <div>
    <span class='polymarker-round' style='background-color: ${color}'></span>
    <strong>${name}</strong>
  </div>
`;

  const prepareFloorNode = (n) => {
    const node = n;
    resetFloorData();
    if (!node.maps || node.maps?.length === 0) {
      console.log("Node doesn't have map details::", n);
      setFloorImageMissing(true);
      return;
    }

    if (!window.mapboxgl) {
      console.error("Mapbox GL JS is not loaded.");
      return;
    }
    window.mapboxgl.accessToken = mapboxApiKey;
    // initialise mapbox
    const tfloorMap = new window.mapboxgl.Map({
      container: "floor-map",
      zoom: 1,
      center: [-75.789, 41.874],
      animate: false
    });
    tfloorMap.addControl(new window.mapboxgl.NavigationControl());

    const drawCpy = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: false
      }
    });
    tfloorMap.addControl(drawCpy);

    tfloorMap.on("draw.create", addArea);
    tfloorMap.on("delete", deleteArea);
    tfloorMap.on("update", updateArea);

    setDraw(drawCpy);

    if (node.maps && node.maps.length) {
      const imgObj = {
        imageUrl: node.maps[0].imagePath,
        mimeType: node.maps[0].mimeType,
        locationId: node.id
      };
      setFloorLoader(true);

      // setLoading(true);
      BusinessHierarchyService.getFloorImageFromS3(imgObj)
        .then((response) => {
          console.log("Floor image", response);
          setFloorLoader(false);
          // setLoading(false);

          let xBounds = 0;
          let yBounds = 0;
          const pixelWidth = node.maps[0].imageWidth;
          const pixelHeight = node.maps[0].imageHeight;
          if (pixelWidth > pixelHeight) {
            xBounds = 4;
            yBounds = (4 * pixelHeight) / pixelWidth;
          } else {
            yBounds = 4;
            xBounds = (4 * pixelWidth) / pixelHeight;
          }

          const southWest = [0, 0];
          const northEast = [xBounds, yBounds];

          const boundsCpy = new window.mapboxgl.LngLatBounds(southWest, northEast);
          bounds = boundsCpy;
          dimensions = {
            width: node.maps[0].width,
            length: node.maps[0].length
          };

          const coords = boundsToCoordinates(boundsCpy.toArray());
          tfloorMap.fitBounds(boundsCpy, { duration: 0 });

          const floorImage = response.data;
          // new Image();
          const floorImgSrc = `data:${imgObj.mimeType};base64,${floorImage}`;

          tfloorMap.addSource("floor-img", {
            type: "image",
            url: floorImgSrc,
            coordinates: coords
          });

          tfloorMap.addLayer({
            id: "floor-img",
            source: "floor-img",
            type: "raster",
            paint: {
              "raster-fade-duration": 0,
              "raster-opacity": 0.85
            }
          });

          setFloorMap(tfloorMap);

          BusinessHierarchyService.getFloorDetails(node.id)
            .then((floorRes) => {
              console.log("Floor Data", floorRes);
              if (floorRes && floorRes.data) {
                const floorData = floorRes.data;
                const geojson = {
                  type: "FeatureCollection",
                  features: []
                };
                if (floorData && floorData.accessPoints && floorData.accessPoints.length) {
                  //
                }
                floorData.accessPoints.forEach((ap) => {
                  if (ap.x && ap.y) {
                    const apFeetCoordinates = {
                      x: ap.x,
                      y: ap.y
                    };

                    // Convert APs from feet data to latlng to plot on map
                    const coordinates = unproject(apFeetCoordinates);

                    // Add APs to feature collection
                    const geometry = {
                      type: "Point",
                      coordinates: [coordinates.lng, coordinates.lat]
                    };

                    const feature = {
                      geometry,
                      properties: ap,
                      type: "Feature"
                    };

                    geojson.features.push(feature);
                  }
                });

                // add AP markers to map
                geojson.features.forEach((marker) => {
                  // create a HTML element for each feature
                  const el = document.createElement("div");
                  el.className = "marker1";
                  el.style.backgroundImage = `url(${APIcon})`;
                  el.style.width = "30px";
                  el.style.height = "30px";
                  el.style.backgroundSize = "100%";

                  // make a marker for each feature and add it to the map
                  const apMarker = new window.mapboxgl.Marker(el)
                    .setLngLat(marker.geometry.coordinates)
                    .setPopup(
                      new window.mapboxgl.Popup({
                        offset: 25
                      }) // add popups
                        .setHTML(`
  <div><strong>Name:</strong> ${marker.properties.name}</div>
  <div><strong>Mac Address:</strong> ${marker.properties.macAddress}</div>
`)
                    )
                    .addTo(tfloorMap);

                  currentAPMarkers.push(apMarker);
                });
              }
            })
            .catch((err) => {
              console.error("Error", err);
            });

          if (node.maps) {
            // prepare zones
            const polygonGeoJson = { type: "FeatureCollection", features: [] };
            const polygonZones = [];

            if (node.type[0].toLowerCase() === "floor" && node.childCount > 0) {
              if (node.children && node.children.length) {
                node.children.forEach((childData) => {
                  if (childData && childData.type[0] === "POLYGON_ZONE" && childData.zoneDetails) {
                    polygonZones.push(childData);
                  }
                });
              } else {
                node.childData.forEach((child) => {
                  const { id } = child;
                  const childData = locationRowMap[id];
                  if (childData && childData.type[0] === "POLYGON_ZONE" && childData.zoneDetails) {
                    polygonZones.push(childData);
                  }
                });
              }

              if (polygonZones && polygonZones.length) {
                polygonZones.forEach((zoneData) => {
                  // Add a data source containing GeoJSON data.
                  const zoneId = `zone-${zoneData.id}`;
                  allZones.push(zoneId);
                  const zoneColor = zoneData.zoneDetails.color ? zoneData.zoneDetails.color : defaultZoneColor;
                  const zoneCoordinatesXYZ = zoneData.zoneDetails.zoneCoordinates && zoneData.zoneDetails.zoneCoordinates.length ? zoneData.zoneDetails.zoneCoordinates : [];
                  const zCoords = [];

                  if (zoneCoordinatesXYZ.length) {
                    zoneCoordinatesXYZ.forEach((coord) => {
                      const coordsSplit = coord.split(",");
                      const mapCoords = unproject({
                        x: coordsSplit[0],
                        y: coordsSplit[1]
                      });
                      zCoords.push([mapCoords.lng, mapCoords.lat]);
                    });

                    // get average of coords
                    let xTotal = 0;
                    let yTotal = 0;
                    for (let i = 0; i < zCoords.length; i++) {
                      xTotal += zCoords[i][0];
                      yTotal += zCoords[i][1];
                    }
                    const xAvg = xTotal / zCoords.length;
                    const yAvg = yTotal / zCoords.length;

                    const feature = {
                      type: "Feature",
                      properties: {
                        id: zoneData.id,
                        name: zoneData.name,
                        zoneData
                      },
                      geometry: {
                        type: "Point",
                        coordinates: [xAvg, yAvg]
                      }
                    };
                    polygonGeoJson.features.push(feature);
                    console.log("zCoords::", zCoords);
                    console.log("Feature::", feature);

                    tfloorMap.addSource(zoneId, {
                      type: "geojson",
                      data: {
                        type: "Feature",
                        geometry: {
                          type: "Polygon",
                          coordinates: [zCoords]
                        }
                      }
                    });

                    // Add a new layer to visualize the polygon.
                    tfloorMap.addLayer({
                      id: zoneId,
                      type: "fill",
                      source: zoneId, // reference the data source
                      layout: {},
                      paint: {
                        "fill-color": zoneColor, // zone color fill
                        "fill-opacity": 0.8,
                        "fill-outline-color": "#999"
                      }
                    });
                  }
                });
              }
            }

            // Add markers to the map.
            polygonGeoJson.features.forEach(({ properties, geometry }) => {
              const el = createMarkerElement();
              const { zoneData } = properties;
              const zoneColor = zoneData.zoneDetails.color || DEFAULT_ZONE_COLOR;

              el.addEventListener("click", handleMarkerClick({ zoneData }));

              const polyMarker = new window.mapboxgl.Marker(el)
                .setLngLat(geometry.coordinates)
                .setPopup(new window.mapboxgl.Popup({ offset: 25 }).setHTML(createPopupHTML(properties.name, zoneColor)))
                .addTo(tfloorMap);

              polyGeoMarkers.push(polyMarker);
              const markerDiv = polyMarker.getElement();

              markerDiv.addEventListener("mouseenter", () => polyMarker.togglePopup());
              markerDiv.addEventListener("mouseleave", () => polyMarker.togglePopup());
            });
          } else {
            console.log("else block");
          }
        })
        .catch((error) => {
          setFloorLoader(false);
          setFloorImageMissing(true);
          // setLoading(false);
          console.log("BusinessHierarchyService: Error on getFloorImageFromS3", error);
        });
    }
    // setFloorMap(floorMap);
  };

  const goToSetupPage = () => {
    navigate("/setup/wirelessnetwork");
  };

  const floorEmptyStateTemplate = () => {
    return (
      <Container>
        <Flex align="center" gap={24}>
          <Empty.Info />
          <Flex align="flex-start" direction="vertical" gap={24}>
            <Flex direction="vertical" gap={8}>
              <Heading>{i18n.t("location-map.no-floor-maps-configured")}</Heading>
              <Text
                color="light"
                size="p2"
                style={{
                  marginRight: 12
                }}
              >
                {i18n.t("location-map.configure-floor-maps")}
              </Text>
            </Flex>
            <Button onClick={() => goToSetupPage()}>{i18n.t("location-map.go-to-setup-section")}</Button>
          </Flex>
        </Flex>
      </Container>
    );
  };

  const zoneFormTemplate = () => {
    return (
      <Drawer className="zone-drawer" onClose={deleteFeature} isOpen={showZoneForm} size="sm">
        <Drawer.Heading>
          <Heading size="primary">Zone Name</Heading>
        </Drawer.Heading>
        <Drawer.Content>
          <div style={{ marginTop: "12px" }} className="zone-form-container">
            <div className="zone-form">
              <div className="zone-input-group">
                <Input placeholder="Zone Name" label="Zone Name" type="text" value={zoneForm.name} onChange={(e) => updateZoneName(e.target.value)} />
              </div>
              <div className="zone-color-picker">
                {/* The label should describe the group of controls */}
                <span id="zone-label-1" className="zone-label">
                  Overlay Color
                </span>
                <div role="group" aria-labelledby="zone-label-1">
                  {zoneColors.map((color) => (
                    <span key={uuidv4()} className="color-container">
                      <span
                        role="button"
                        tabIndex={0} // Make the span focusable
                        onClick={() => selectZoneColor(color)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            selectZoneColor(color);
                          }
                        }} // Handle keyboard events
                        className="zone-color"
                        style={{
                          backgroundColor: color,
                          border: zoneForm.color === color ? "1px solid #000" : "0"
                        }}
                        aria-label="select color" // Accessible label for each color swatch
                        id={`color-${color}`} // Unique ID for each color swatch
                      />
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Drawer.Content>
        <Drawer.Footer>
          <Button.Group>
            <Button kind="secondary" onClick={deleteFeature}>
              Cancel
            </Button>
            {zoneFormAction === "update" && (
              <Button kind="primary" onClick={saveZone}>
                Save
              </Button>
            )}
            {zoneFormAction === "create" && (
              <Button kind="primary" onClick={createZone}>
                Save
              </Button>
            )}
          </Button.Group>
        </Drawer.Footer>
      </Drawer>
    );
  };

  useEffect(() => {
    console.log("Floor location", nodeData);
    console.log(`${floorMap} : ${floorImageMissing}`);
    if (nodeData && nodeData.data) {
      prepareFloorNode(nodeData.data);
    }
  }, []);

  return (
    <div className="floor-container">
      <div id="floor-map" className="floor-map">
        {floorLoader && (
          <div className="floor-loader">
            <MSpinner size="md" />
          </div>
        )}
        {floorImageMissing && floorEmptyStateTemplate()}
      </div>
      {showZoneForm && zoneFormTemplate()}
    </div>
  );
}

export default FloorMap;
